import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Edit,
  MinusCircle, Car, Globe, Phone, RotateCcw, Pause, Play, CheckCircle, XCircle,
  AlertTriangle,
} from 'lucide-react';
import { confidentialAPI } from '../../services/api';
import { formatDate, toLocalISODate, parseLocalDate } from '../../utils/dateUtils';
import toast from '../../utils/toast';
import LoadingSpinner from '../UI/LoadingSpinner';
import { StatusBadge } from '../UI/StatusBadge';
import Modal from '../UI/Modal';
import ConfirmModal from '../UI/ConfirmModal';
import DatePickerField from '../UI/DatePickerField';
import FormSelect from '../UI/FormSelect';
import SearchableCombobox from '../UI/SearchableCombobox';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { FilterButton } from '../UI/FilterButton';
import { InfoBadge } from '../UI/InfoBadge';
import { useStaffList } from '../../hooks/useStaffData';

const DEDUCTION_TYPE_OPTIONS = [
  { value: 'SEASON_PARKING', label: 'Season Parking' },
  { value: 'GLOBAL_SMS', label: 'Global SMS' },
  { value: 'IDD_CHARGES', label: 'IDD Charges' },
  { value: 'SALARY_OVERPAYMENT_RECOVERY', label: 'Salary Overpayment Recovery' },
];

const FREQUENCY_OPTIONS = [
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'AD_HOC', label: 'Ad-Hoc' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const DEDUCTION_TYPE_ICONS = {
  SEASON_PARKING: Car,
  GLOBAL_SMS: Globe,
  IDD_CHARGES: Phone,
  SALARY_OVERPAYMENT_RECOVERY: RotateCcw,
};

const STATUS_MAP = {
  ACTIVE: 'active',
  PAUSED: 'on_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount || 0);

const EMPTY_FORM = {
  employee_staff_id: '',
  deduction_type: '',
  frequency: '',
  amount: '',
  effective_start_date: null,
  effective_end_date: null,
  remarks: '',
};

const DeductionManagementTab = () => {
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ deduction_type: '', frequency: '', status: '' });

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: staffData, isLoading: staffLoading } = useStaffList(
    { page_size: 500, employment_status: 'active' }
  );
  const employees = useMemo(() => {
    const staff = staffData?.results || [];
    return staff.filter(e => e.employment_classification !== 'system');
  }, [staffData]);

  const getEmployeeName = useCallback((staffId) => {
    const emp = employees.find(e => e.staff_id === staffId);
    if (!emp) return staffId;
    return `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || staffId;
  }, [employees]);

  const [confirmAction, setConfirmAction] = useState(null);

  const fetchDeductions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.deduction_type) params.deduction_type = filters.deduction_type;
      if (filters.frequency) params.frequency = filters.frequency;
      if (filters.status) params.status = filters.status;
      if (searchTerm) params.search = searchTerm;

      const res = await confidentialAPI.getPayrollDeductions(params);
      setDeductions(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error('Failed to fetch deductions:', err);
      toast.error('Failed to load deductions');
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => { fetchDeductions(); }, [fetchDeductions]);

  const activeFilterCount = [filters.deduction_type, filters.frequency, filters.status].filter(Boolean).length;

  const openCreateModal = () => {
    setEditingDeduction(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setShowFormModal(true);
  };

  const openEditModal = (deduction) => {
    setEditingDeduction(deduction);
    setFormData({
      employee_staff_id: deduction.employee_staff_id,
      deduction_type: deduction.deduction_type,
      frequency: deduction.frequency,
      amount: deduction.amount,
      effective_start_date: deduction.effective_start_date ? parseLocalDate(deduction.effective_start_date) : null,
      effective_end_date: deduction.effective_end_date ? parseLocalDate(deduction.effective_end_date) : null,
      remarks: deduction.remarks || '',
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.employee_staff_id) errors.employee_staff_id = 'Please select an employee.';
    if (!formData.deduction_type) errors.deduction_type = 'Deduction type is required.';
    if (!formData.frequency) errors.frequency = 'Frequency is required.';
    if (!formData.amount || parseFloat(formData.amount) <= 0) errors.amount = 'Amount must be greater than 0.';
    if (!formData.effective_start_date) errors.effective_start_date = 'Start date is required.';
    if (formData.frequency === 'AD_HOC' && !formData.effective_end_date) {
      errors.effective_end_date = 'End date is required for ad-hoc deductions.';
    }
    if (formData.effective_start_date && formData.effective_end_date && formData.effective_end_date < formData.effective_start_date) {
      errors.effective_end_date = 'End date cannot be before start date.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        employee_staff_id: formData.employee_staff_id.trim(),
        deduction_type: formData.deduction_type,
        frequency: formData.frequency,
        amount: parseFloat(formData.amount).toFixed(2),
        effective_start_date: toLocalISODate(formData.effective_start_date),
        effective_end_date: formData.effective_end_date ? toLocalISODate(formData.effective_end_date) : null,
        remarks: formData.remarks,
      };

      if (editingDeduction) {
        await confidentialAPI.updatePayrollDeduction(editingDeduction.id, payload);
        toast.success('Deduction updated successfully');
      } else {
        await confidentialAPI.createPayrollDeduction(payload);
        toast.success('Deduction created successfully');
      }

      setShowFormModal(false);
      fetchDeductions();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const fieldErrors = {};
        Object.entries(detail).forEach(([key, val]) => {
          fieldErrors[key] = Array.isArray(val) ? val.join(' ') : String(val);
        });
        setFormErrors(fieldErrors);
      } else {
        toast.error('Failed to save deduction');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (deduction, newStatus) => {
    try {
      await confidentialAPI.updatePayrollDeduction(deduction.id, { status: newStatus });
      toast.success(`Deduction ${newStatus.toLowerCase()}`);
      fetchDeductions();
    } catch {
      toast.error('Failed to update deduction status');
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDelete = async (deduction) => {
    try {
      await confidentialAPI.deletePayrollDeduction(deduction.id);
      toast.success('Deduction deleted');
      fetchDeductions();
    } catch {
      toast.error('Failed to delete deduction');
    } finally {
      setConfirmAction(null);
    }
  };

  const summaryStats = {
    total: deductions.length,
    active: deductions.filter(d => d.status === 'ACTIVE').length,
    recurring: deductions.filter(d => d.frequency === 'RECURRING' && d.status === 'ACTIVE').length,
    adHoc: deductions.filter(d => d.frequency === 'AD_HOC' && d.status === 'ACTIVE').length,
    monthlyTotal: deductions
      .filter(d => d.status === 'ACTIVE')
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0),
  };

  if (loading && deductions.length === 0) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <MinusCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.active}</p>
              <p className="text-xs text-gray-500">Active Deductions</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.recurring}</p>
              <p className="text-xs text-gray-500">Recurring</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <MinusCircle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.adHoc}</p>
              <p className="text-xs text-gray-500">Ad-Hoc</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <MinusCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.monthlyTotal)}</p>
              <p className="text-xs text-gray-500">Monthly Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 max-w-xl">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by employee ID or remarks..."
            theme="admin"
          />
        </div>
        <div className="flex items-center gap-2">
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={activeFilterCount}
            onClick={() => setShowFilters(v => !v)}
          />
          <button onClick={openCreateModal} className="btn btn-admin-primary">
            <Plus className="w-4 h-4" />
            New Deduction
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormSelect
            value={filters.deduction_type}
            onChange={(val) => setFilters(f => ({ ...f, deduction_type: val }))}
            options={[{ value: '', label: 'All Types' }, ...DEDUCTION_TYPE_OPTIONS]}
            placeholder="Deduction Type"
            theme="admin"
          />
          <FormSelect
            value={filters.frequency}
            onChange={(val) => setFilters(f => ({ ...f, frequency: val }))}
            options={[{ value: '', label: 'All Frequencies' }, ...FREQUENCY_OPTIONS]}
            placeholder="Frequency"
            theme="admin"
          />
          <FormSelect
            value={filters.status}
            onChange={(val) => setFilters(f => ({ ...f, status: val }))}
            options={STATUS_OPTIONS}
            placeholder="Status"
            theme="admin"
          />
        </div>
      )}

      {/* Deductions Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden p-4 space-y-3">
          {deductions.map((ded) => {
            const TypeIcon = DEDUCTION_TYPE_ICONS[ded.deduction_type] || MinusCircle;
            return (
              <div key={ded.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{getEmployeeName(ded.employee_staff_id)}</p>
                    <p className="text-xs text-gray-500">{ded.employee_staff_id}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <StatusBadge status={STATUS_MAP[ded.status] || ded.status} label={ded.status_display} />
                  </div>
                </div>
                <div className="mt-3 space-y-2 flex-1">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Type</span>
                    <span className="text-gray-900 text-right truncate min-w-0 flex items-center justify-end gap-2">
                      <TypeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      {ded.deduction_type_display}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Frequency</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ded.frequency === 'RECURRING'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {ded.frequency_display}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Amount</span>
                    <span className="text-gray-900 text-right truncate min-w-0 font-semibold tabular-nums">
                      {formatCurrency(ded.amount)}{ded.frequency === 'RECURRING' ? '/mo' : ''}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Effective Period</span>
                    <span className="text-gray-900 text-right min-w-0">
                      {formatDate(ded.effective_start_date)}
                      {' — '}
                      {ded.effective_end_resolved ? formatDate(ded.effective_end_resolved) : 'Ongoing'}
                      {ded.is_capped_by_employment_end && (
                        <span className="ml-1" title="End date derived from employment end date">
                          <InfoBadge color="orange" label="Employment End" />
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {(ded.status === 'ACTIVE' || ded.status === 'PAUSED') && (
                  <div className="card-action-footer justify-end gap-2">
                    {ded.status === 'ACTIVE' && (
                      <button
                        onClick={() => setConfirmAction({ type: 'pause', deduction: ded })}
                        className="icon-button-admin"
                        title="Pause"
                        aria-label="Pause deduction"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    )}
                    {ded.status === 'PAUSED' && (
                      <button
                        onClick={() => setConfirmAction({ type: 'resume', deduction: ded })}
                        className="icon-button-admin"
                        title="Resume"
                        aria-label="Resume deduction"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(ded)}
                      className="icon-button-admin"
                      title="Edit"
                      aria-label="Edit deduction"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmAction({ type: 'cancel', deduction: ded })}
                      className="icon-button-danger"
                      title="Cancel"
                      aria-label="Cancel deduction"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {deductions.map((ded) => {
                const TypeIcon = DEDUCTION_TYPE_ICONS[ded.deduction_type] || MinusCircle;
                return (
                  <tr key={ded.id} className="table-row">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{getEmployeeName(ded.employee_staff_id)}</span>
                        <span className="text-xs text-gray-400">{ded.employee_staff_id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{ded.deduction_type_display}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        ded.frequency === 'RECURRING'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {ded.frequency_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(ded.amount)}{ded.frequency === 'RECURRING' ? '/mo' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          {formatDate(ded.effective_start_date)}
                          {' — '}
                          {ded.effective_end_resolved ? formatDate(ded.effective_end_resolved) : 'Ongoing'}
                        </span>
                        {ded.is_capped_by_employment_end && (
                          <span title="End date derived from employment end date">
                            <InfoBadge color="orange" label="Employment End" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={STATUS_MAP[ded.status] || ded.status} label={ded.status_display} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        {ded.status === 'ACTIVE' && (
                          <button
                            onClick={() => setConfirmAction({ type: 'pause', deduction: ded })}
                            className="icon-button-admin"
                            title="Pause"
                            aria-label="Pause deduction"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        {ded.status === 'PAUSED' && (
                          <button
                            onClick={() => setConfirmAction({ type: 'resume', deduction: ded })}
                            className="icon-button-admin"
                            title="Resume"
                            aria-label="Resume deduction"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        {(ded.status === 'ACTIVE' || ded.status === 'PAUSED') && (
                          <>
                            <button
                              onClick={() => openEditModal(ded)}
                              className="icon-button-admin"
                              title="Edit"
                              aria-label="Edit deduction"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setConfirmAction({ type: 'cancel', deduction: ded })}
                              className="icon-button-danger"
                              title="Cancel"
                              aria-label="Cancel deduction"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {deductions.length === 0 && !loading && (
          <div className="text-center py-16">
            <MinusCircle className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No deductions found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm || activeFilterCount > 0
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating the first payroll deduction.'}
            </p>
            {!searchTerm && activeFilterCount === 0 && (
              <button onClick={openCreateModal} className="btn btn-admin-primary mt-4">
                <Plus className="w-4 h-4" />
                New Deduction
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <DeductionFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        onSave={handleSave}
        saving={saving}
        isEdit={!!editingDeduction}
        employees={employees}
        employeesLoading={staffLoading}
      />

      {/* Confirm Action Modal */}
      {confirmAction?.type === 'pause' && (
        <ConfirmModal
          isOpen
          title="Pause Deduction"
          confirmLabel="Pause"
          variant="primary"
          onConfirm={() => handleStatusChange(confirmAction.deduction, 'PAUSED')}
          onCancel={() => setConfirmAction(null)}
        >
          <p className="text-sm text-gray-700">
            Pause the <strong>{confirmAction.deduction.deduction_type_display}</strong> deduction
            for <strong>{confirmAction.deduction.employee_staff_id}</strong>?
            It will not be applied in future payroll runs until resumed.
          </p>
        </ConfirmModal>
      )}

      {confirmAction?.type === 'resume' && (
        <ConfirmModal
          isOpen
          title="Resume Deduction"
          confirmLabel="Resume"
          variant="primary"
          onConfirm={() => handleStatusChange(confirmAction.deduction, 'ACTIVE')}
          onCancel={() => setConfirmAction(null)}
        >
          <p className="text-sm text-gray-700">
            Resume the <strong>{confirmAction.deduction.deduction_type_display}</strong> deduction
            for <strong>{confirmAction.deduction.employee_staff_id}</strong>?
            It will be applied in the next payroll run.
          </p>
        </ConfirmModal>
      )}

      {confirmAction?.type === 'cancel' && (
        <ConfirmModal
          isOpen
          title="Cancel Deduction"
          confirmLabel="Cancel Deduction"
          variant="danger"
          onConfirm={() => handleStatusChange(confirmAction.deduction, 'CANCELLED')}
          onCancel={() => setConfirmAction(null)}
        >
          <p className="text-sm text-gray-700">
            Permanently cancel the <strong>{confirmAction.deduction.deduction_type_display}</strong> deduction
            ({formatCurrency(confirmAction.deduction.amount)}/period) for <strong>{confirmAction.deduction.employee_staff_id}</strong>?
            This cannot be undone.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};


const DeductionFormModal = ({ isOpen, onClose, formData, setFormData, formErrors, onSave, saving, isEdit, employees = [], employeesLoading }) => {
  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const editDisplayValue = useMemo(() => {
    if (!isEdit || !formData.employee_staff_id) return undefined;
    const match = employees.find(e => e.staff_id === formData.employee_staff_id);
    if (match) {
      const name = match.alias?.trim() || `${match.first_name || ''} ${match.last_name || ''}`.trim();
      return `${name} (${match.staff_id})`;
    }
    return formData.employee_staff_id;
  }, [isEdit, formData.employee_staff_id, employees]);

  const footer = (
    <>
      <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={saving}>
        Cancel
      </button>
      <button type="button" onClick={onSave} className="btn btn-admin-primary" disabled={saving}>
        {saving ? (
          <>
            <span className="loading-spinner-sm mr-2" />
            Saving...
          </>
        ) : (
          isEdit ? 'Update Deduction' : 'Create Deduction'
        )}
      </button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Deduction' : 'New Payroll Deduction'} size="lg" footer={footer} theme="admin">
      <div className="space-y-5">
        {/* Employee Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <SearchableCombobox
            variant="form"
            theme="admin"
            items={employees}
            value={formData.employee_staff_id}
            onChange={(val) => update('employee_staff_id', val)}
            placeholder="Search by name or staff ID..."
            searchPlaceholder="Search employees..."
            emptyLabel="No employees found"
            displayFn={(emp) => {
              const name = emp.alias?.trim() || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
              return `${name} (${emp.staff_id})`;
            }}
            searchFn={(emp, term) =>
              emp.first_name?.toLowerCase().includes(term) ||
              emp.last_name?.toLowerCase().includes(term) ||
              emp.alias?.toLowerCase().includes(term) ||
              emp.staff_id?.toLowerCase().includes(term) ||
              `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(term)
            }
            valueFn={(emp) => emp.staff_id}
            disabled={isEdit}
            loading={employeesLoading}
            loadingLabel="Loading employees..."
            displayValue={isEdit ? editDisplayValue : undefined}
            className="w-full"
            renderItem={({ item, itemValue, isSelected, isHighlighted, onSelect }) => (
              <button
                key={itemValue}
                type="button"
                onClick={onSelect}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                  isSelected ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700'
                } ${isHighlighted ? 'bg-gray-100' : ''}`}
              >
                <span>{item.alias?.trim() || `${item.first_name || ''} ${item.last_name || ''}`.trim()}</span>
                <span className="text-gray-400 text-xs ml-2">{item.staff_id}</span>
              </button>
            )}
          />
          {formErrors.employee_staff_id && <p className="mt-1 text-xs text-red-600">{formErrors.employee_staff_id}</p>}
        </div>

        {/* Type + Frequency row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Type</label>
            <FormSelect
              value={formData.deduction_type}
              onChange={(val) => update('deduction_type', val)}
              options={DEDUCTION_TYPE_OPTIONS}
              placeholder="Select type"
              theme="admin"
            />
            {formErrors.deduction_type && <p className="mt-1 text-xs text-red-600">{formErrors.deduction_type}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <FormSelect
              value={formData.frequency}
              onChange={(val) => update('frequency', val)}
              options={FREQUENCY_OPTIONS}
              placeholder="Select frequency"
              theme="admin"
            />
            {formErrors.frequency && <p className="mt-1 text-xs text-red-600">{formErrors.frequency}</p>}
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (SGD)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => update('amount', e.target.value)}
            className={`input-admin w-full ${formErrors.amount ? 'border-red-300' : ''}`}
            placeholder="70.00"
          />
          {formErrors.amount && <p className="mt-1 text-xs text-red-600">{formErrors.amount}</p>}
        </div>

        {/* Date row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective Start Date</label>
            <DatePickerField
              selected={formData.effective_start_date}
              onChange={(date) => update('effective_start_date', date)}
              placeholderText="Select start date"
              theme="admin"
            />
            {formErrors.effective_start_date && <p className="mt-1 text-xs text-red-600">{formErrors.effective_start_date}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Effective End Date
              {formData.frequency === 'RECURRING' && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
            </label>
            <DatePickerField
              selected={formData.effective_end_date}
              onChange={(date) => update('effective_end_date', date)}
              placeholderText={formData.frequency === 'RECURRING' ? 'Leave blank for ongoing' : 'Select end date'}
              isClearable
              theme="admin"
            />
            {formErrors.effective_end_date && <p className="mt-1 text-xs text-red-600">{formErrors.effective_end_date}</p>}
          </div>
        </div>

        {/* Contextual hint */}
        {formData.frequency === 'AD_HOC' && (
          <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Ad-hoc deductions require an end date. Set it to the last day of the target payroll month
              (e.g. 31 Mar 2026 for a March charge).
            </span>
          </div>
        )}

        {formData.frequency === 'RECURRING' && !formData.effective_end_date && formData.effective_start_date && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              This recurring deduction will auto-apply to every payroll run from {formatDate(formData.effective_start_date)} onwards until paused or cancelled.
            </span>
          </div>
        )}

        {/* Remarks */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
          <textarea
            value={formData.remarks}
            onChange={(e) => update('remarks', e.target.value)}
            className="textarea-admin w-full"
            placeholder="e.g. Monthly IDD charges for corporate line"
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DeductionManagementTab;
